import { buildUrl, defaultFetchHeaders, fetchJson } from "../../../utils";
import { centsFromApi } from "../../currency";
import { hasOrganizationFinancialAccess } from "../../access";
import { getEffectiveVendUserSnapshot } from "../../effectiveUser";
import {
  Integration,
  TelemetryMethod,
  TelemetryMethodConfigPayload,
  TelemetryMethodSyncUpdate,
  TelemetryMonitorStatusResponse,
} from "../../types";

const backend = process.env.REACT_APP_BACKEND_URL || "";
const telemetryBase = `${backend}/telemetry`;

export function normalizeTelemetryMethod(entry: any): TelemetryMethod {
  return {
    id: entry.id,
    methodType: entry.method_type ?? entry.methodType,
    status: entry.status,
    enabled: !!entry.enabled,
    priority: Number(entry.priority ?? 0),
    frequency: entry.frequency ?? null,
    publicConfig: entry.public_config ?? entry.publicConfig ?? {},
    maskedPrivateConfig:
      entry.masked_private_config ?? entry.maskedPrivateConfig ?? {},
    hasPrivateConfig: !!(entry.has_private_config ?? entry.hasPrivateConfig),
    lastSyncAt: entry.last_sync_at ?? entry.lastSyncAt ?? null,
    lastReceivedAt: entry.last_received_at ?? entry.lastReceivedAt ?? null,
    lastError: entry.last_error ?? entry.lastError ?? null,
  };
}

export function normalizeTelemetryMethodSyncUpdate(
  entry: any
): TelemetryMethodSyncUpdate {
  return {
    id: entry.id,
    telemetryId: entry.telemetry_id ?? entry.telemetryId,
    provider: entry.provider ?? entry.service_type ?? entry.serviceType ?? "",
    methodType: entry.method_type ?? entry.methodType,
    status: entry.status,
    enabled: !!entry.enabled,
    priority: Number(entry.priority ?? 0),
    lastSyncAt: entry.last_sync_at ?? entry.lastSyncAt ?? null,
    lastReceivedAt: entry.last_received_at ?? entry.lastReceivedAt ?? null,
  };
}

function normalizeTelemetryConnection(entry: any): Integration {
  return {
    id: entry.id,
    name: entry.name ?? entry.title,
    service_type: entry.service_type ?? entry.serviceType,
    api_key: entry.api_key ?? entry.apiKey,
    has_secret: entry.has_secret ?? entry.hasSecret,
    card_transaction_surcharge_cents: centsFromApi(
      entry.card_transaction_surcharge_cents ?? entry.cardTransactionSurchargeCents,
    ),
    monitor: entry.monitor,
    monitor_frequency: entry.monitor_frequency ?? entry.monitorFrequency,
    methods: (entry.methods || []).map(normalizeTelemetryMethod),
    updated_at: entry.updated_at ?? entry.updatedAt,
  };
}

export async function fetchTelemetryConnections(): Promise<Integration[]> {
  if (!hasOrganizationFinancialAccess(getEffectiveVendUserSnapshot())) return [];
  const headers = await defaultFetchHeaders();
  if (!hasOrganizationFinancialAccess(getEffectiveVendUserSnapshot())) return [];
  const list = await fetchJson<any[]>(
    buildUrl(`${telemetryBase}/all`, {}),
    {
      method: "GET",
      credentials: "include",
      headers,
    },
    "Failed to fetch telemetry connections"
  );
  if (!hasOrganizationFinancialAccess(getEffectiveVendUserSnapshot())) return [];
  return (list || []).map(normalizeTelemetryConnection);
}

export async function createTelemetryConnection(payload: {
  name?: string;
  apiKey: string;
  serviceType: string;
  cardTransactionSurchargeCents?: number;
  applyCardTransactionSurchargeToMachines?: boolean;
}): Promise<Integration> {
  const body = {
    name: payload.name,
    api_key: payload.apiKey,
    service_type: (payload.serviceType || "").toLowerCase(),
    card_transaction_surcharge_cents: centsFromApi(payload.cardTransactionSurchargeCents),
    apply_card_transaction_surcharge_to_machines:
      !!payload.applyCardTransactionSurchargeToMachines,
  };
  const entry = await fetchJson<any>(
    `${telemetryBase}/create`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        ...(await defaultFetchHeaders()),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
    "Failed to create telemetry connection"
  );
  return normalizeTelemetryConnection(entry);
}

export async function editTelemetryConnection(
  id: string,
  payload: {
    name?: string;
    apiKey: string;
    serviceType: string;
    cardTransactionSurchargeCents?: number;
    applyCardTransactionSurchargeToMachines?: boolean;
  }
): Promise<Integration> {
  const body = {
    name: payload.name,
    api_key: payload.apiKey,
    service_type: (payload.serviceType || "").toLowerCase(),
    card_transaction_surcharge_cents: centsFromApi(payload.cardTransactionSurchargeCents),
    apply_card_transaction_surcharge_to_machines:
      !!payload.applyCardTransactionSurchargeToMachines,
  };
  const entry = await fetchJson<any>(
    buildUrl(`${telemetryBase}/edit`, { id }),
    {
      method: "PUT",
      credentials: "include",
      headers: {
        ...(await defaultFetchHeaders()),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
    "Failed to update telemetry connection"
  );
  return normalizeTelemetryConnection(entry);
}

export async function fetchTelemetryMethods(
  telemetryId: string
): Promise<TelemetryMethod[]> {
  const list = await fetchJson<any[]>(
    buildUrl(`${telemetryBase}/methods`, { telemetry_id: telemetryId }),
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to fetch telemetry methods"
  );
  return (list || []).map(normalizeTelemetryMethod);
}

export async function updateTelemetryMethodConfig(
  id: string,
  payload: TelemetryMethodConfigPayload
): Promise<TelemetryMethod> {
  const body = {
    enabled: payload.enabled,
    status: payload.status,
    priority: payload.priority,
    frequency: payload.frequency,
    public_config: payload.publicConfig,
    private_config: payload.privateConfig,
  };
  const entry = await fetchJson<any>(
    buildUrl(`${telemetryBase}/method/config`, { id }),
    {
      method: "PUT",
      credentials: "include",
      headers: {
        ...(await defaultFetchHeaders()),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
    "Failed to update telemetry method"
  );
  return normalizeTelemetryMethod(entry);
}

export async function revealTelemetryMethodPrivateConfig(
  id: string
): Promise<Record<string, unknown>> {
  const entry = await fetchJson<any>(
    buildUrl(`${telemetryBase}/method/private-config`, { id }),
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to reveal telemetry method private config"
  );
  return entry.private_config ?? entry.privateConfig ?? {};
}

export async function deleteTelemetryConnection(id: string): Promise<void> {
  await fetchJson<{ status?: string }>(
    buildUrl(`${telemetryBase}/delete`, { id }),
    {
      method: "DELETE",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to delete telemetry connection"
  );
}

export async function startTelemetryMonitor(
  id: string
): Promise<TelemetryMonitorStatusResponse> {
  return fetchJson<TelemetryMonitorStatusResponse>(
    buildUrl(`${telemetryBase}/monitor/start`, { id }),
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to start telemetry monitor"
  );
}

export async function stopTelemetryMonitor(
  id: string
): Promise<TelemetryMonitorStatusResponse> {
  return fetchJson<TelemetryMonitorStatusResponse>(
    buildUrl(`${telemetryBase}/monitor/stop`, { id }),
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to stop telemetry monitor"
  );
}
